import { useQuery } from '@apollo/client';
import { useOneTimeCookie } from '@do/seashell/hooks';
import { setCookieValue } from '@do/seashell/utils';
import { Background, StepTransitionManager, style } from '@do/walrus';
import _get from 'lodash/get';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  APP_PLATFORM_TEMPLATE_COOKIE,
  CHURN_SURVEY_URL,
  GITHUB_STUDENT_PACK,
  ONBOARDING_ORIGIN_PARAM
} from '../constants';
import copy from '../copy';
import { GetLoginInfo } from '../generated-types/GetLoginInfo';
import {
  registerReferralCode,
  registerReferralCodeVariables
} from '../generated-types/registerReferralCode';
import useActivationRedirectCookie from '../hooks/use-activation-redirect-cookie';
import useDocumentTitle from '../hooks/use-document-title';
import useGithubStudentCookie from '../hooks/use-github-student-cookie';
import useSegmentTracking from '../hooks/use-segment-tracking';
import useSift from '../hooks/use-sift';
import { LoginType } from '../interfaces/login';
import {
  Variant,
  VariantBackgroundColor,
  VariantBackgroundImage,
  VariationTemplates
} from '../interfaces/variant';
import {
  newBrand,
  githubLoginVariation as githubVariation
} from '../login-variants';
import {
  GET_LOGIN_FORM_DATA,
  REGISTER_REFERRAL_CODE
} from '../queries/queries';
import { getRefcode } from '../utils/get-refcode';
import { getSessionData } from '../utils/gql-utils';
import { getQueryParam } from '../utils/query-utils';
import { getRandomLoginVariant } from '../utils/string-utils';
import {
  BannerType,
  Column,
  ErrorBanner,
  FormWrap,
  HeaderBanner,
  Loading,
  PaddedContents,
  Row,
  Wrap,
  WrapSplit
} from './Common';
import Header from './Header';
import LoginForm from './LoginForm';
import { Splash } from './Splash';
import TwoFactorAuthForm from './TwoFactorAuthForm';
import VerificationForm from './VerificationForm';
import NewErrorBanner from './newBrandDesign/ErrorBanner';
import NewFormWrap from './newBrandDesign/FormWrap';

const APP_PLATFORM_TEMPLATE_URL_REGEX = /^https:\/\/cloud.digitalocean.com\/apps\/new\?(template|repo)=https:\/\/github\.com/;

const STEPS = {
  login: 0,
  two_factor_auth: 1,
  two_factor_auth_secondary: 2,
  device_verification: 3
};

let variant: Variant;

const WrapComponentContent: React.FC<{
  children: React.ReactNode;
  step: number;
  isNewBrandDesignEnabled: boolean;
}> = ({ children, step, isNewBrandDesignEnabled }) => {
  const WrapComponent =
    variant?.splash?.version === VariationTemplates.SPLIT ? WrapSplit : Wrap;

  const isNewBrand =
    isNewBrandDesignEnabled && variant.version === VariationTemplates.NEW_BRAND;
  const FormWrapComponent = isNewBrand ? NewFormWrap : FormWrap;

  return (
    <WrapComponent>
      <FormWrapComponent style={{ paddingBottom: `${style.vars.space['3']}` }}>
        <StepTransitionManager currentStepNum={step}>
          <PaddedContents>{children}</PaddedContents>
        </StepTransitionManager>
      </FormWrapComponent>
    </WrapComponent>
  );
};

const Login: React.FC<{ isNewBrandDesignEnabled?: boolean }> = ({
  isNewBrandDesignEnabled = false
}) => {
  const location = useLocation();
  const { search } = location;

  const refcode = getRefcode(search);
  useQuery<registerReferralCode, registerReferralCodeVariables>(
    REGISTER_REFERRAL_CODE,
    {
      variables: { refcode },
      skip: !refcode
    }
  );

  useActivationRedirectCookie();
  const onboardingOrigin = getQueryParam(ONBOARDING_ORIGIN_PARAM, search);

  const { loading: isLoading, data } = useQuery<GetLoginInfo>(
    GET_LOGIN_FORM_DATA,
    {
      variables: {
        onboarding_origin: onboardingOrigin,
        redirect_url: getQueryParam('redirect_url', search)
      }
    }
  );

  const isGitHubStudentVariant = onboardingOrigin === GITHUB_STUDENT_PACK;
  useGithubStudentCookie(isGitHubStudentVariant);

  const isUserAccountDeactivated = useOneTimeCookie(
    '_digitalocean_flash_code',
    'user_account_deactivated'
  );

  const [step, setStep] = React.useState(STEPS.login);
  const [config2fa, setConfig2fa] = React.useState<LoginType>();

  useDocumentTitle(copy.form.login_title);
  useSift(_get(data, 'loginFormData.session_id'));
  variant = isGitHubStudentVariant
    ? githubVariation
    : isNewBrandDesignEnabled
    ? newBrand
    : getRandomLoginVariant();

  useSegmentTracking('Login Variant', {
    variantType: _get(variant, 'type')
  });

  const preferredLoginState = _get(location, 'state.preferredLoginState');
  const user = _get(data, 'loginFormData.user');

  const hasPreferredLogin =
    preferredLoginState !== 'none' &&
    !isLoading &&
    !!_get(user, 'email') &&
    !!_get(user, 'sign_in_method') &&
    !!_get(user, 'avatar_url');

  /**
   * This handles the case of a new-to-DO user clicking on a "Deploy to DO"
   * button elsewhere on the internet.
   *
   * - The user clicks on the button and is directed to a URL like this:
   *     https://cloud.digitalocean.com/apps/new?template=https://github.com/RocketChat/Rocket.Chat/tree/master
   * - The Edge gateway detects that the user is not logged in and redirects
   *   them to the login form with a `redirect_url` param like this:
   *     https://cloud.digitalocean.com/login?redirect_url=https%3A%2F%2Fcloud.digitalocean.com%2Fapps%2Fnew%3Ftemplate%3Dhttps%3A%2F%2Fgithub.com%2FRocketChat%2FRocket.Chat%2Ftree%2Fmaster
   * - We detect that the `redirect_url` param is a "Deploy to DO" URL.
   * - We set a cookie with the complete redirect URL.
   *
   * Later on in the signup flow, once the user's account is successfully
   * activated, the onboarding app will use this cookie to redirect the user
   * back to the "Deploy to DO" create flow in the App Platform UI.
   */
  useEffect(() => {
    const redirectUrl = getQueryParam('redirect_url', search);

    if (!redirectUrl || !APP_PLATFORM_TEMPLATE_URL_REGEX.test(redirectUrl)) {
      return;
    }

    setCookieValue(APP_PLATFORM_TEMPLATE_COOKIE, redirectUrl);
  });

  if (isLoading) {
    return <Loading isLogin={true} />;
  }

  const googleOauthUrl = `/sessions/google${search}`;
  const githubOauthUrl = `/sessions/github${search}`;

  const oauthError = _get(data, 'loginFormData.oauth_error');
  const userUUID = getSessionData('ajs_user_id').replace(/(^"|"$)/g, '');
  const churnSurvey = userUUID
    ? `${CHURN_SURVEY_URL}?uuid=${userUUID}`
    : CHURN_SURVEY_URL;

  let form;
  const ErrorBannerComponent = isNewBrandDesignEnabled
    ? NewErrorBanner
    : ErrorBanner;

  switch (step) {
    case STEPS.login:
      form = (
        <>
          {oauthError && (
            <ErrorBannerComponent
              error={copy.errors[oauthError] || copy.errors.unknown}
            />
          )}
          <LoginForm
            googleOauthUrl={googleOauthUrl}
            githubOauthUrl={githubOauthUrl}
            hasPreferredLogin={hasPreferredLogin}
            user={user}
            team={_get(data, 'loginFormData.team')}
            passwordSuccessBanner={_get(location, 'state.success')}
            showNext={(nextStep: LoginType) => {
              if (STEPS[nextStep.type] === STEPS.two_factor_auth) {
                setConfig2fa(nextStep);
              }
              setStep(STEPS[nextStep.type]);
            }}
            variant={variant}
            isNewBrandDesignEnabled={isNewBrandDesignEnabled}
          />
        </>
      );
      break;
    case STEPS.two_factor_auth:
      form = config2fa && (
        <TwoFactorAuthForm
          key={step}
          changeStep={() => setStep(STEPS.two_factor_auth_secondary)}
          type={config2fa.primary}
          nextStepType={config2fa.secondary}
          isNewBrandDesignEnabled={isNewBrandDesignEnabled}
        />
      );

      break;
    case STEPS.two_factor_auth_secondary:
      form = config2fa && config2fa.secondary && (
        <TwoFactorAuthForm
          key={step}
          changeStep={() => setStep(STEPS.two_factor_auth)}
          type={config2fa.secondary}
          nextStepType={config2fa.primary}
          isSecondary
          isNewBrandDesignEnabled={isNewBrandDesignEnabled}
        />
      );
      break;
    case STEPS.device_verification:
      form = (
        <VerificationForm
          goBack={() => setStep(STEPS.login)}
          isNewBrandDesignEnabled={isNewBrandDesignEnabled}
        />
      );
      break;
  }

  return (
    <>
      <Header isLogin={true} queryString={search} />
      {isUserAccountDeactivated && (
        <HeaderBanner
          type={BannerType.Success}
          text={copy.deactivation_message}
          linkText={copy.survey_text}
          url={churnSurvey}
        />
      )}
      {variant?.splash?.version !== VariationTemplates.SPLIT && (
        <Background
          url={(variant.background as VariantBackgroundImage).image}
          horizontalPosition={
            (variant.background as VariantBackgroundImage).horizontalPosition
          }
          verticalPosition={
            (variant.background as VariantBackgroundImage).verticalPosition
          }
        />
      )}
      {variant?.splash?.version === VariationTemplates.SPLIT &&
      variant.splash ? (
        <Row>
          <Column
            style={{
              background: (variant?.background as VariantBackgroundColor)?.color
                ? (variant.background as VariantBackgroundColor).color
                : 'unset'
            }}
          >
            <WrapComponentContent
              step={step}
              isNewBrandDesignEnabled={isNewBrandDesignEnabled}
            >
              {form}
            </WrapComponentContent>
          </Column>
          <Splash variant={variant.splash} />
        </Row>
      ) : (
        <WrapComponentContent
          step={step}
          isNewBrandDesignEnabled={isNewBrandDesignEnabled}
        >
          {form}
        </WrapComponentContent>
      )}
    </>
  );
};

export default Login;
